import {ClassName, DefaultChildren} from '#/utils/utils';
import {CheckIcon} from '@heroicons/react/24/solid';
import {motion} from 'framer-motion';
import {FunctionComponent, ReactNode} from 'react';

export const LoadingDots: FunctionComponent<
  {gapClassName?: `gap-${number}`; widthClassName?: `w-${number}`} & ClassName
> = ({className = '', gapClassName = 'gap-3', widthClassName = 'w-3'}) => {
  return (
    <span className={`flex ${gapClassName} ${className}`}>
      {Array.from(Array(3)).map((_, i) => (
        <motion.span
          key={i}
          animate={{
            translateY: [0, -20, 0, 0, 0],
          }}
          transition={{
            delay: i * 0.1,
            repeat: Infinity,
          }}
          className={`bg-deepblue ${widthClassName} aspect-square rounded-full`}
        />
      ))}
    </span>
  );
};

export const DotsLoadingIndicator: FunctionComponent<
  {children: {title: ReactNode; subtitle: ReactNode}} & ClassName
> = ({className, children: {title, subtitle}}) => {
  return (
    <div className={`flex flex-col items-center gap-4 ${className}`}>
      <LoadingDots className='h-10 items-end mb-6' />
      {title && <span className='text-xl font-bold'>{title}</span>}
      {subtitle && <span className='text-sm font-bold'>{subtitle}</span>}
    </div>
  );
};

export const SuccessIndicator: FunctionComponent<
  {
    indicatorBackgroundColor?: string;
    widthClassName?: `w-${string | number}`;
  } & DefaultChildren
> = ({children, indicatorBackgroundColor = 'bg-deepblue', widthClassName = 'w-[120px]'}) => {
  return (
    <div className='flex flex-col items-center gap-4'>
      <span className='flex gap-3 h-10 items-end mb-6 mt-20'>
        <motion.span
          initial={{
            scale: 0.8,
            padding: '48px',
            visibility: 'hidden',
          }}
          animate={{
            scale: 1.0,
            padding: '24px',
            visibility: 'visible',
          }}
          transition={{
            duration: 1,
            type: 'spring',
            bounce: 0.5,
          }}
          className={`${widthClassName} ${indicatorBackgroundColor} aspect-square rounded-full flex-center`}
        >
          <CheckIcon className='w-full aspect-square fill-white stroke-white' />
        </motion.span>
      </span>
      <motion.span
        initial={{
          translateX: '-50%',
          opacity: 0,
          visibility: 'hidden',
        }}
        animate={{
          translateX: '0px',
          opacity: 1,
          visibility: 'visible',
        }}
        transition={{
          duration: 1,
          type: 'spring',
          bounce: 0.25,
        }}
        className='text-xl font-bold'
      >
        {children}
      </motion.span>
    </div>
  );
};
